export type PopupPlacement = { left: number; top: number; placedAt: number };

// Placements are keyed by popup id because one terminal can have several popups open off a single click point.
export type PopupClickPoint = {
  x: number;
  y: number;
  placements?: Record<string, PopupPlacement>;
};

export function createPopupClickPoint(
  previous: PopupClickPoint | null | undefined,
  x: number,
  y: number,
  reopenedPopupId?: string
): PopupClickPoint {
  const placements = { ...previous?.placements };
  if (reopenedPopupId) {
    delete placements[reopenedPopupId];
  }
  return { x, y, placements };
}

const POPUP_SETTLE_MS = 600;

export function positionTerminalPopup(
  element: HTMLElement,
  terminalUid: string,
  showFilter: boolean,
  clickPoint: PopupClickPoint | null = null,
  bounds: { left: number; right: number; top: number; bottom: number } | null = null
): void {
  element.style.display = 'block';
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  let windowWidth = window.innerWidth || document.documentElement.clientWidth;
  if (showFilter) {
    windowWidth = windowWidth - 400;
  }
  const containingBlock = element.offsetParent?.getBoundingClientRect();
  const viewportPadding = 12;
  const leftBound = bounds ? bounds.left + viewportPadding : viewportPadding;
  const rightBound = bounds ? bounds.right - viewportPadding : windowWidth - viewportPadding;
  const topBound = bounds ? bounds.top + viewportPadding : viewportPadding;
  const bottomBound = bounds ? bounds.bottom - viewportPadding : windowHeight - viewportPadding;

  if (clickPoint && containingBlock) {
    // After the open settles, the popup keeps a fixed offset from its terminal; never re-derive it from stale viewport coords.
    clickPoint.placements ??= {};
    const placed = clickPoint.placements[terminalUid];
    if (placed && Date.now() - placed.placedAt > POPUP_SETTLE_MS) {
      element.style.setProperty('top', placed.top + 'px', 'important');
      element.style.setProperty('--popup-left', placed.left + 'px');
      return;
    }
    const placedAt = placed?.placedAt ?? Date.now();

    // CSS zoom on an ancestor scales local px (left/top/offsetWidth) relative to viewport px.
    const parentWidth = (element.offsetParent as HTMLElement).offsetWidth;
    const scale = parentWidth > 0 && containingBlock.width > 0 ? containingBlock.width / parentWidth : 1;
    const gap = 10;
    const popupWidth = element.offsetWidth * scale;
    const popupHeight = element.offsetHeight * scale;

    const spaceBelow = bottomBound - clickPoint.y;
    const spaceAbove = clickPoint.y - topBound;
    const opensAbove = spaceBelow < popupHeight + gap && spaceAbove > spaceBelow;

    const rawTop = opensAbove ? clickPoint.y - popupHeight - gap : clickPoint.y + gap;
    const maxTop = Math.max(topBound, bottomBound - popupHeight);
    const safeTop = Math.min(Math.max(rawTop, topBound), maxTop);
    const relTop = (safeTop - containingBlock.top) / scale;
    element.style.setProperty('top', relTop + 'px', 'important');

    const maxLeft = Math.max(leftBound, rightBound - popupWidth);
    const safeLeft = Math.min(Math.max(clickPoint.x - popupWidth / 2, leftBound), maxLeft);
    const relLeft = (safeLeft - containingBlock.left) / scale;
    element.style.setProperty('--popup-left', relLeft + 'px');
    clickPoint.placements[terminalUid] = { left: relLeft, top: relTop, placedAt };
    return;
  }

  //no click point available: just keep whatever position the CSS gave it clamped on-screen horizontally
  if (containingBlock) {
    const rect = element.getBoundingClientRect();
    const currentLeft = rect.left - containingBlock.left;
    const minimumLeft = leftBound - containingBlock.left;
    const maximumLeft = rightBound - element.clientWidth - containingBlock.left;
    const safeLeft = Math.min(Math.max(currentLeft, minimumLeft), Math.max(minimumLeft, maximumLeft));
    element.style.setProperty('--popup-left', safeLeft + 'px');
  }
}

export function startPopupShiftWatch(
  anchorEl: HTMLElement,
  onShift: (deltaX: number, deltaY: number) => void
): () => void {
  let lastRect = anchorEl.getBoundingClientRect();
  let frameId: number | null = null;
  let stopped = false;
  const checkPosition = () => {
    frameId = null;
    if (stopped) {
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    const deltaX = rect.left - lastRect.left;
    const deltaY = rect.top - lastRect.top;
    lastRect = rect;
    if (deltaX || deltaY) {
      onShift(deltaX, deltaY);
    }
  };
  const scheduleCheck = () => {
    if (!stopped && frameId === null) {
      frameId = requestAnimationFrame(checkPosition);
    }
  };
  const onLayoutEvent = (event: Event) => {
    if (event.target instanceof Element && event.target.contains(anchorEl)) {
      scheduleCheck();
    }
  };
  const resizeObserver = new ResizeObserver(scheduleCheck);
  const mutationObserver = new MutationObserver(scheduleCheck);
  let ancestor: HTMLElement | null = anchorEl;
  while (ancestor) {
    resizeObserver.observe(ancestor);
    mutationObserver.observe(ancestor, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
      childList: true
    });
    ancestor = ancestor.parentElement;
  }
  window.addEventListener('resize', scheduleCheck);
  window.addEventListener('scroll', scheduleCheck, true);
  window.addEventListener('transitionend', onLayoutEvent, true);
  window.addEventListener('animationend', onLayoutEvent, true);
  return () => {
    stopped = true;
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    window.removeEventListener('resize', scheduleCheck);
    window.removeEventListener('scroll', scheduleCheck, true);
    window.removeEventListener('transitionend', onLayoutEvent, true);
    window.removeEventListener('animationend', onLayoutEvent, true);
  };
}



